// TODO(CXP-2396): Uncomment specific products imports when re-enabling the feature
// import type { ApplicableProduct } from '@ekhodealer/ekho-common';
// import { Button, Dropdown, Group, RadioGroup, Stack, Text, TextInput } from '@ekhodealer/ekho-common/rev';
// import { useMemo, useState } from 'react';
// import { useGetApplicableProducts } from '../../api/useGetApplicableProducts';
// import { AddProductsModal } from '../AddProductsModal';
import { useCallback } from 'react';

import { Dropdown, Group, Stack, Text, TextInput } from '@ekhodealer/ekho-common/rev';

import { useDiscountFieldError, useDiscountFormField } from '../../atoms/discountFormAtoms';

const MAX_PERCENTAGE = 100;
const MIN_AMOUNT = 0;

const AMOUNT_TYPE_OPTIONS = [
	{ id: 'fixed', label: 'Fixed amount' },
	{ id: 'percentage', label: 'Percentage' },
];

// TODO(CXP-2396): Uncomment when re-enabling specific products selection
// const APPLIES_TO_ITEMS = [
// 	{ value: 'all', label: 'All products' },
// 	{ value: 'specific', label: 'Specific products' },
// ];

export const DiscountValueSection = () => {
	const [amountType, setAmountType] = useDiscountFormField('amountType');
	const [amount, setAmount] = useDiscountFormField('amount');
	// appliesTo is always 'all' for now — specific products feature is incomplete
	// TODO(CXP-2396): Uncomment when re-enabling specific products selection
	// const [appliesTo, setAppliesTo] = useDiscountFormField('appliesTo');
	// const [applicableProducts, setApplicableProducts] = useDiscountFormField('applicableProducts');
	const amountError = useDiscountFieldError('amount');

	/** Reject negative values for all types; reject values > 100 for percentages. */
	const handleAmountChange = useCallback(
		(value: string): void => {
			// Allow empty string so the user can clear the field
			if (value === '') {
				setAmount(value);
				return;
			}

			const numericValue = Number(value);
			if (Number.isNaN(numericValue)) return;

			if (numericValue < MIN_AMOUNT) return;
			if (amountType === 'percentage' && numericValue > MAX_PERCENTAGE) return;

			setAmount(value);
		},
		[amountType, setAmount],
	);

	/** On blur, clamp the percentage value to 0-100 in case of edge cases. */
	const handleAmountBlur = useCallback((): void => {
		if (amountType !== 'percentage' || amount === '') return;

		const numericValue = Number(amount);
		if (Number.isNaN(numericValue)) return;

		const clamped = Math.max(MIN_AMOUNT, Math.min(numericValue, MAX_PERCENTAGE));
		if (clamped !== numericValue) {
			setAmount(String(clamped));
		}
	}, [amountType, amount, setAmount]);

	// TODO(CXP-2396): Uncomment when re-enabling specific products selection
	// const { data: allProducts } = useGetApplicableProducts();
	// const selectedProducts = useMemo(
	// 	() =>
	// 		(allProducts ?? []).filter((product: ApplicableProduct) =>
	// 			applicableProducts.includes(product.id),
	// 		),
	// 	[allProducts, applicableProducts],
	// );
	// const [isBrowseModalOpen, setIsBrowseModalOpen] = useState(false);
	// const removeProduct = (productId: string) => {
	// 	setApplicableProducts(applicableProducts.filter((id) => id !== productId));
	// };
	// const handleBrowse = () => {
	// 	setIsBrowseModalOpen(true);
	// };
	// const handleProductsConfirm = (productIds: string[]) => {
	// 	setApplicableProducts(productIds);
	// };

	return (
		<Stack itemsSpacing='16'>
			<Text size='body' weight='semibold'>
				2. Discount value
			</Text>

			<Group itemsSpacing='8' noWrap>
				<Group.Item grow={2}>
					<Dropdown
						options={AMOUNT_TYPE_OPTIONS}
						value={amountType}
						onSelectionChange={(value) => {
							const newType = value as 'fixed' | 'percentage';
							setAmountType(newType);
							// Round and clamp existing amount when switching to percentage
							// (percentage mask only allows integers 0-100)
							if (newType === 'percentage' && amount !== '') {
								const numericValue = Number(amount);
								if (!Number.isNaN(numericValue)) {
									const clamped = Math.min(Math.round(numericValue), MAX_PERCENTAGE);
									setAmount(String(Math.max(MIN_AMOUNT, clamped)));
								}
							}
						}}
						size='medium'
					/>
				</Group.Item>
				<Group.Item grow={1}>
					<TextInput
						type={amountType === 'percentage' ? 'percentage' : 'text'}
						value={amount}
						onChange={handleAmountChange}
						onBlur={handleAmountBlur}
						placeholder={amountType === 'percentage' ? '0' : '0.00'}
						leftContent={amountType === 'fixed' ? <Text>$</Text> : undefined}
						size='medium'
						hasError={amountError}
					/>
				</Group.Item>
			</Group>

			<Text size='footnote' color='secondary'>
				Applies to all products
			</Text>

			{/* TODO(CXP-2396): Uncomment when re-enabling specific products selection
				<Stack itemsSpacing='8'>
					<Text size='footnote'>Applies to</Text>
					<RadioGroup
						value={appliesTo}
						onChange={(value) => setAppliesTo(value as 'all' | 'specific')}
						items={APPLIES_TO_ITEMS}
					/>
				</Stack>

				{appliesTo === 'specific' && (
					<Stack itemsSpacing='12'>
						<Group itemsSpacing='8' noWrap>
							<Group.Item grow={1}>
								<div onClick={handleBrowse} role='button' tabIndex={-1}>
									<TextInput
										type='search'
										placeholder='Search products'
										size='medium'
									/>
								</div>
							</Group.Item>
							<Button variant='secondary' size='medium' onPress={handleBrowse}>
								Browse
							</Button>
						</Group>

						{selectedProducts.map((product: ApplicableProduct) => (
							<Group key={product.id} itemsAlignX='space-between'>
								<Group itemsSpacing='8'>
									<img
										src={product.imageUrl}
										alt={product.name}
										width={40}
										height={40}
									/>
									<Stack>
										<Text size='bodySmall'>{product.name}</Text>
										<Text size='footnote' color='secondary'>
											{product.retailPrice
												? `$${product.retailPrice}`
												: '\u2014'}
										</Text>
									</Stack>
								</Group>
								<Button
									variant='ghost'
									size='icon'
									onPress={() => removeProduct(product.id)}>
									✕
								</Button>
							</Group>
						))}
					</Stack>
				)}
			*/}

			{/* TODO(CXP-2396): Uncomment when re-enabling specific products selection
			<AddProductsModal
				isOpen={isBrowseModalOpen}
				onOpenChange={setIsBrowseModalOpen}
				selectedProductIds={applicableProducts}
				onConfirm={handleProductsConfirm}
			/>
			*/}
		</Stack>
	);
};
